// Reunderwriting Module (Tier 3) PR 4 — Pipeline metrics section.
// Bar chart of cases-per-stage + four "totals" tiles. Bar clicks navigate
// to the pipeline page filtered to the clicked stage.

import { useNavigate } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/design-system/components/ui";
import { chartTheme } from "@/pages/dealFlow/metrics/chartTheme";
import { useReunderwritingPipelineMetrics } from "@/hooks/reunderwriting/useReunderwriting";
import type {
  ReunderwritingMetricsFilter,
  ReunderwritingStageBucket,
} from "@/types/reunderwriting";

interface Props {
  filter: ReunderwritingMetricsFilter;
}

export function PipelineSection({ filter }: Props) {
  const navigate = useNavigate();
  const q = useReunderwritingPipelineMetrics(filter);

  if (q.isLoading) return <Loading />;
  if (q.isError || !q.data) return <ErrorState />;
  const { stages, totalActive, totalCompleted, totalDeclined, totalWithdrawn } = q.data;

  return (
    <div className="flex flex-col gap-space-4" data-testid="reunderwriting-metrics-pipeline">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-space-3">
        <Tile label="Active" value={totalActive} />
        <Tile label="Completed" value={totalCompleted} />
        <Tile label="Declined" value={totalDeclined} />
        <Tile label="Withdrawn" value={totalWithdrawn} />
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Cases per stage</CardTitle>
          <p className="text-small text-gray-mid mt-space-1">
            Click a bar to drill through to the Pipeline filtered to that stage.
          </p>
        </CardHeader>
        <CardContent>
          {stages.every((s) => s.caseCount === 0) ? (
            <EmptyState label="No cases in this window." />
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={stages} layout="vertical" margin={{ left: 16, right: 16 }}>
                <CartesianGrid stroke={chartTheme.grid} strokeDasharray="3 3" />
                <XAxis type="number" stroke={chartTheme.axis} />
                <YAxis dataKey="label" type="category" width={120} stroke={chartTheme.axis} />
                <Tooltip />
                <Bar
                  dataKey="caseCount"
                  cursor="pointer"
                  onClick={(entry) => {
                    const stage = (entry as unknown as ReunderwritingStageBucket).stage;
                    navigate(`/reunderwriting/pipeline?stage=${stage}`);
                  }}
                >
                  {stages.map((d) => (
                    <Cell key={d.stage} fill={chartTheme.primary} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Tile({ label, value }: { label: string; value: number }) {
  return (
    <Card>
      <CardContent>
        <div className="text-label uppercase tracking-label text-gray-mid">{label}</div>
        <div className="font-display text-h2 text-onyx">{value}</div>
      </CardContent>
    </Card>
  );
}

function Loading() {
  return (
    <div className="flex h-64 items-center justify-center text-small text-gray-mid">
      Loading pipeline metrics…
    </div>
  );
}

function ErrorState() {
  return (
    <div className="flex h-64 items-center justify-center text-small text-status-error">
      Failed to load pipeline metrics.
    </div>
  );
}

function EmptyState({ label }: { label: string }) {
  return (
    <div className="flex h-[260px] items-center justify-center text-small text-gray-mid">
      {label}
    </div>
  );
}
